/**
 * FloatingUtilityDock — small stack in the bottom-right of every logged-in
 * page. Exposes:
 *   * ReportBug — floating "🐞" button opens a bug-report modal.
 *   * Support ticket — same modal, `type='support'`, opened via a global
 *     custom event (`fda:open-report`) so any page (e.g. HelpCenter) can
 *     imperatively pop it.
 *
 * Payload lands in the shared `/api/bug-reports` collection with a `type`
 * field (`bug` | `support`) so admins triage both in one inbox.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Bug, Loader2, HelpCircle, LifeBuoy } from 'lucide-react';
const COPY = {
  bug: {
    eyebrow:     'Report a bug',
    title:       'Something broken?',
    description: 'The more detail you give us, the faster we can fix it. Screenshot optional.',
    titlePh:     'Short summary of what broke',
    descLabel:   'What happened?',
    stepsLabel:  'Steps to reproduce (optional)',
    submitLabel: 'Send report',
    toast:       'Thanks — the team has been notified.',
    icon:        Bug,
  },
  support: {
    eyebrow:     'Contact support',
    title:       'How can we help?',
    description: 'Send us a support ticket and we\u2019ll get back to you by email — usually within one business day.',
    titlePh:     'Short subject line',
    descLabel:   'Describe what you need help with',
    stepsLabel:  'Anything else you\u2019ve tried? (optional)',
    submitLabel: 'Send ticket',
    toast:       'Ticket received — we\u2019ll reply by email shortly.',
    icon:        LifeBuoy,
  },
};

const FloatingUtilityDock = () => {
  const { API, user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('bug'); // 'bug' | 'support'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Global event bus — any page can dispatch:
  //   window.dispatchEvent(new CustomEvent('fda:open-report', {detail:{type:'bug'|'support'}}))
  useEffect(() => {
    const handler = (e) => {
      const nextMode = e?.detail?.type === 'support' ? 'support' : 'bug';
      setMode(nextMode);
      setOpen(true);
    };
    window.addEventListener('fda:open-report', handler);
    return () => window.removeEventListener('fda:open-report', handler);
  }, []);

  if (!user) return null;
  const copy = COPY[mode] || COPY.bug;

  const reset = () => { setTitle(''); setDescription(''); setSteps(''); setFile(null); };

  const submit = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSubmitting(true);
    try {
      let screenshot_url = null;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          const up = await axios.post(`${API}/uploads/forum`, fd);
          screenshot_url = up.data?.url || up.data?.download_url || null;
        } catch { /* upload failed, submit without image */ }
      }
      await axios.post(`${API}/bug-reports`, {
        type:            mode,
        title:           title.trim(),
        description:     description.trim(),
        steps_to_repro:  steps.trim() || null,
        page_url:        window.location.href,
        screenshot_url,
        browser_info:    navigator.userAgent,
      });
      toast.success(copy.toast);
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Floating buttons removed — the top-nav bar now hosts the Bug + Support
          triggers. This dock only renders the modal + listens for the global
          `fda:open-report` custom event so any page can open it. */}

      <DialogContent className="max-w-lg" data-testid={`report-modal-${mode}`}>
        <div className="eyebrow text-primary inline-flex items-center gap-1.5">
          <HelpCircle className="w-3 h-3" /> {copy.eyebrow}
        </div>
        <DialogTitle className="font-heading text-xl font-extrabold mt-1">
          {copy.title}
        </DialogTitle>
        <DialogDescription className="text-xs text-muted-foreground">
          {copy.description}
        </DialogDescription>

        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={copy.titlePh} data-testid="report-title" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{copy.descLabel}</label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="report-description" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{copy.stepsLabel}</label>
            <Textarea rows={2} value={steps} onChange={(e) => setSteps(e.target.value)} data-testid="report-steps" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Screenshot (optional)</label>
            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="report-file" />
          </div>
          <div className="text-[11px] text-muted-foreground">
            Page: <span className="font-mono">{pathname}</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full font-bold">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-full font-bold" data-testid="report-submit">
            {submitting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</> : copy.submitLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FloatingUtilityDock;
